/**
 * VoicesPage (epic-6 §7 / VO-2) — CRUD; create/edit dialog uploads a WAV sample
 * to a storage collection (voice-samples) → fileId; activate/deactivate a voice;
 * "Test" synthesizes a short sample and plays it with an <audio> player;
 * "Synthesize" runs a full synthesis with optional emitTo routing.
 */
import { useRef, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Plus,
  Pencil,
  Trash2,
  Mic,
  Upload,
  Loader2,
  Play,
  Power,
  PowerOff,
  Waves,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetVoicesQuery,
  useCreateVoiceMutation,
  useUpdateVoiceMutation,
  useDeleteVoiceMutation,
  useTestVoiceMutation,
  useSynthesizeVoiceMutation,
  type SynthesizeResult,
} from '@/store/api/voiceApi';
import { useGetStorageCollectionsQuery } from '@/store/api/apiSlice';
import { uploadSingle } from '@/lib/storage-transfer';
import type { Voice, VoiceTestResult, VoiceSource } from '@/types';
import { format } from 'date-fns';

/** Placeholder for the service-side default TTS model (see voice-service config). */
const DEFAULT_TTS_MODEL = 'qwen3-tts-vc-realtime-2026-01-15';
const PROVIDER_VOICE_HELP =
  'ID de voz clonada/enrolada en DashScope; con un sample y sin ID se clona al crear; sin sample se usa la voz por defecto';

interface StorageCollectionOption {
  _id: string;
  name: string;
}

function getVoiceId(v: Voice): string {
  return v.id || v._id || '';
}

/** Extract an HTTP status from an RTK Query / Nest error payload. */
function apiStatus(err: unknown): number | undefined {
  const e = err as { status?: number | string; data?: { statusCode?: number } };
  if (typeof e?.status === 'number') return e.status;
  if (typeof e?.data?.statusCode === 'number') return e.data.statusCode;
  return undefined;
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { data?: { message?: string | string[] } };
  const m = e?.data?.message;
  if (Array.isArray(m)) return m.join(', ');
  return typeof m === 'string' && m.length > 0 ? m : fallback;
}

/** Map a test-synthesis failure to an actionable message. */
function describeTestError(err: unknown): string {
  const status = apiStatus(err);
  if (status === 404) return 'La voz no está activa. Actívala para poder sintetizar.';
  if (status === 503) {
    return 'Configura la colección de salida (VOICE_OUTPUT_COLLECTION_ID) o selecciona una colección.';
  }
  return errorMessage(err, 'La síntesis falló.');
}

/** Map a synthesize failure to an actionable message. */
function describeSynthesizeError(err: unknown): string {
  const status = apiStatus(err);
  if (status === 404) return 'La voz no está activa. Actívala para poder sintetizar.';
  if (status === 503) {
    return 'Configura la colección de salida (VOICE_OUTPUT_COLLECTION_ID) o usa emitTo storage con una colección.';
  }
  return errorMessage(err, 'La síntesis falló.');
}

/**
 * Surface which provider resolution produced the audio:
 *  - green badge when a cloned voice (or the voice's own providerVoiceId) spoke;
 *  - amber warning when the default voice was used (the clone did NOT run).
 * Renders nothing while no synthesis result is available.
 */
function SourceIndicator({ source, providerVoiceId }: { source?: VoiceSource; providerVoiceId?: string }) {
  if (source === 'clone' || (source === 'providerVoiceId' && !!providerVoiceId)) {
    return (
      <p
        data-testid="voice-source-clone"
        className="rounded-md border border-green-500/40 bg-green-50 px-3 py-2 text-xs text-green-800 dark:bg-green-950 dark:text-green-100"
      >
        Clon: se sintetizó con la voz clonada de este sample.
      </p>
    );
  }
  if (source === 'default') {
    return (
      <p
        data-testid="voice-source-default"
        className="rounded-md border border-yellow-500/40 bg-yellow-50 px-3 py-2 text-xs text-yellow-800 dark:bg-yellow-950 dark:text-yellow-100"
      >
        Voz por defecto (no se clonó): esta voz no tiene un{' '}
        <span className="font-mono">providerVoiceId</span> ni un clon disponible.
      </p>
    );
  }
  return null;
}

export function VoicesPage() {
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Voice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Voice | null>(null);
  const [testTarget, setTestTarget] = useState<Voice | null>(null);
  const [synthTarget, setSynthTarget] = useState<Voice | null>(null);

  const { data: voices, isLoading } = useGetVoicesQuery();
  const [deleteVoice, { isLoading: isDeleting }] = useDeleteVoiceMutation();
  const [updateVoice, { isLoading: isToggling }] = useUpdateVoiceMutation();

  const voiceList = (voices as Voice[] | undefined) ?? [];

  async function handleToggleActive(voice: Voice) {
    try {
      await updateVoice({ id: getVoiceId(voice), body: { isActive: !voice.isActive } }).unwrap();
      toast.success(voice.isActive ? 'Voice deactivated' : 'Voice activated');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to update voice'));
    }
  }

  const columns: ColumnDef<Voice, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Mic className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium">{row.original.name}</p>
            {row.original.description && (
              <p className="text-xs text-muted-foreground truncate max-w-xs">{row.original.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'language',
      header: 'Language',
      cell: ({ row }) => <Badge variant="outline" className="text-xs">{row.original.language ?? 'es'}</Badge>,
    },
    {
      accessorKey: 'fileId',
      header: 'Sample',
      cell: ({ row }) =>
        row.original.fileId ? (
          <span className="font-mono text-[11px] text-muted-foreground">{row.original.fileId.slice(0, 12)}…</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'providerVoiceId',
      header: 'Provider',
      cell: ({ row }) => {
        const providerVoiceId = row.original.providerVoiceId;
        const ttsModel = row.original.ttsModel;
        return (
          <div className="flex flex-col">
            {providerVoiceId ? (
              <span className="font-mono text-[11px] text-muted-foreground" title={providerVoiceId}>
                {providerVoiceId.slice(0, 14)}…
              </span>
            ) : (
              <span className="text-xs text-muted-foreground" title="Sin providerVoiceId: se usa la voz por defecto">
                default
              </span>
            )}
            {ttsModel && (
              <span className="text-[10px] text-muted-foreground/70" title={ttsModel}>
                {ttsModel}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'default' : 'secondary'} className="text-xs">
          {row.original.isActive ? 'active' : 'inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (row.original.createdAt ? format(new Date(row.original.createdAt), 'MMM d, yyyy') : '—'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const isActive = row.original.isActive;
        return (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              title={isActive ? 'Deactivate voice' : 'Activate voice'}
              aria-label={isActive ? 'Deactivate voice' : 'Activate voice'}
              onClick={() => handleToggleActive(row.original)}
              disabled={isToggling}
            >
              {isActive ? (
                <Power className="h-4 w-4 text-green-600" />
              ) : (
                <PowerOff className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title={isActive ? 'Test synthesize' : 'Activate the voice to test it'}
              aria-label="Test synthesize"
              disabled={!isActive}
              onClick={() => setTestTarget(row.original)}
            >
              <Play className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title={isActive ? 'Synthesize (advanced)' : 'Activate the voice to synthesize'}
              aria-label="Synthesize"
              disabled={!isActive}
              onClick={() => setSynthTarget(row.original)}
            >
              <Waves className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Edit voice" aria-label="Edit voice" onClick={() => setEditTarget(row.original)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Delete voice" aria-label="Delete voice" onClick={() => setDeleteTarget(row.original)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      },
    },
  ];

  async function handleDelete() {
    if (deleteTarget) {
      try {
        await deleteVoice(getVoiceId(deleteTarget)).unwrap();
        toast.success('Voice deleted');
        setDeleteTarget(null);
      } catch (err) {
        toast.error(errorMessage(err, 'Failed to delete voice'));
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Voices</h1>
          <p className="text-sm text-muted-foreground">
            TTS voices with WAV samples and synthesize testing (VO-2)
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Voice
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : voiceList.length === 0 ? (
            <EmptyState
              title="No voices"
              description="Create a voice with a WAV sample to enable synthesis"
              action={<Button onClick={() => setCreateOpen(true)}><Plus className="mr-1 h-4 w-4" /> Create Voice</Button>}
            />
          ) : (
            <DataTable columns={columns} data={voiceList} />
          )}
        </CardContent>
      </Card>

      <CreateVoiceDialog open={createOpen} onOpenChange={setCreateOpen} toast={toast} />

      {editTarget && (
        <EditVoiceDialog
          key={`edit-${getVoiceId(editTarget)}`}
          voice={editTarget}
          onOpenChange={(open) => !open && setEditTarget(null)}
          toast={toast}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Voice"
        description={`Delete voice "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />

      {testTarget && (
        <TestVoiceDialog
          key={`test-${getVoiceId(testTarget)}`}
          voice={testTarget}
          onOpenChange={(open) => !open && setTestTarget(null)}
          toast={toast}
        />
      )}

      {synthTarget && (
        <SynthesizeVoiceDialog
          key={`synth-${getVoiceId(synthTarget)}`}
          voice={synthTarget}
          onOpenChange={(open) => !open && setSynthTarget(null)}
          toast={toast}
        />
      )}
    </div>
  );
}

// ─── Sample uploader (WAV → storage collection → fileId) ──────────────────────

function WavSampleUpload(props: {
  toast: ReturnType<typeof useToast>;
  fileId: string | null;
  onFileIdChange: (id: string | null) => void;
}) {
  const { data: collections } = useGetStorageCollectionsQuery();
  const collectionList = (collections as StorageCollectionOption[] | undefined) ?? [];
  const defaultCollection =
    collectionList.find((c) => c.name.toLowerCase() === 'voice-samples') ?? collectionList[0];

  const [collectionId, setCollectionId] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const targetCollectionId = collectionId || defaultCollection?._id || '';

  async function handleUpload() {
    const input = fileInputRef.current;
    if (!input?.files?.[0]) {
      props.toast.error('Select a WAV file first');
      return;
    }
    if (!targetCollectionId) {
      props.toast.error('No target collection available');
      return;
    }
    setUploading(true);
    try {
      const { fileId } = await uploadSingle(input.files[0], targetCollectionId);
      props.onFileIdChange(fileId);
      props.toast.success('Sample uploaded');
    } catch (err) {
      props.toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <Label>WAV sample</Label>
      {collectionList.length > 0 ? (
        <select
          aria-label="Sample collection"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={targetCollectionId}
          onChange={(e) => setCollectionId(e.target.value)}
        >
          {collectionList.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
      ) : (
        <p className="text-xs text-muted-foreground">No collections available.</p>
      )}
      <div className="flex gap-2">
        <Input
          ref={fileInputRef}
          type="file"
          accept=".wav,audio/wav,audio/x-wav"
          onChange={() => props.onFileIdChange(null)}
        />
        <Button variant="outline" onClick={handleUpload} disabled={uploading}>
          {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
          Upload
        </Button>
      </div>
      {props.fileId ? (
        <p className="text-xs text-green-600">Sample ready — fileId {props.fileId.slice(0, 12)}…</p>
      ) : (
        <p className="text-xs text-muted-foreground">Opcional: sube un WAV para asociarlo a la voz.</p>
      )}
    </div>
  );
}

// ─── Create voice dialog ──────────────────────────────────────────────────────

function CreateVoiceDialog(props: { open: boolean; onOpenChange: (open: boolean) => void; toast: ReturnType<typeof useToast> }) {
  const [createVoice, { isLoading: isCreating }] = useCreateVoiceMutation();
  const [form, setForm] = useState({
    name: '',
    description: '',
    language: 'es',
    providerVoiceId: '',
    ttsModel: '',
  });
  const [fileId, setFileId] = useState<string | null>(null);

  const submit = async () => {
    if (!form.name.trim()) {
      props.toast.error('Name is required');
      return;
    }
    try {
      const created = await createVoice({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        language: form.language.trim() || undefined,
        providerVoiceId: form.providerVoiceId.trim() || undefined,
        ttsModel: form.ttsModel.trim() || undefined,
        fileId: fileId ?? undefined,
      }).unwrap();
      // A providerVoiceId on the response means the sample was cloned eagerly.
      props.toast.success(created?.providerVoiceId ? 'Voice created (voz clonada)' : 'Voice created');
      props.onOpenChange(false);
      setForm({ name: '', description: '', language: 'es', providerVoiceId: '', ttsModel: '' });
      setFileId(null);
    } catch (err) {
      // A sample that could not be cloned now surfaces the backend 400 here.
      props.toast.error(errorMessage(err, 'Failed to create voice'));
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Voice</DialogTitle>
          <DialogDescription>
            Sube un WAV de muestra (colección voice-samples) y registra la voz. Opcional: sin sample se
            usará la voz por defecto (se avisará al sintetizar).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="voice-name">Name</Label>
              <Input
                id="voice-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="voice-language">Language</Label>
              <Input
                id="voice-language"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                placeholder="es"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="voice-description">Description</Label>
            <textarea
              id="voice-description"
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="voice-provider-id">Provider Voice ID</Label>
              <Input
                id="voice-provider-id"
                value={form.providerVoiceId}
                onChange={(e) => setForm({ ...form, providerVoiceId: e.target.value })}
                placeholder="qwen-tts-vc-..."
              />
              <p className="text-xs text-muted-foreground">{PROVIDER_VOICE_HELP}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="voice-tts-model">TTS Model</Label>
              <Input
                id="voice-tts-model"
                value={form.ttsModel}
                onChange={(e) => setForm({ ...form, ttsModel: e.target.value })}
                placeholder={DEFAULT_TTS_MODEL}
              />
              <p className="text-xs text-muted-foreground">Vacío → modelo por defecto del servicio.</p>
            </div>
          </div>
          <WavSampleUpload toast={props.toast} fileId={fileId} onFileIdChange={setFileId} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={isCreating}>{isCreating ? 'Creating…' : 'Create Voice'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit voice dialog ────────────────────────────────────────────────────────

function EditVoiceDialog(props: {
  voice: Voice;
  onOpenChange: (open: boolean) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { voice } = props;
  const [updateVoice, { isLoading }] = useUpdateVoiceMutation();
  const [form, setForm] = useState({
    name: voice.name ?? '',
    description: voice.description ?? '',
    language: voice.language ?? 'es',
    providerVoiceId: voice.providerVoiceId ?? '',
    ttsModel: voice.ttsModel ?? '',
    isActive: voice.isActive,
  });
  const [fileId, setFileId] = useState<string | null>(voice.fileId ?? null);

  const submit = async () => {
    if (!form.name.trim()) {
      props.toast.error('Name is required');
      return;
    }
    const nextProviderVoiceId = form.providerVoiceId.trim();
    // Omit an unchanged providerVoiceId: sending the old value back would look
    // like an explicit override and block the server from re-arming the clone
    // when the WAV sample is replaced.
    const providerVoiceIdUnchanged =
      nextProviderVoiceId === (voice.providerVoiceId ?? '');
    try {
      await updateVoice({
        id: getVoiceId(voice),
        body: {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          language: form.language.trim() || undefined,
          // Empty string clears the provider voice (re-clones / falls back to default).
          ...(providerVoiceIdUnchanged ? {} : { providerVoiceId: nextProviderVoiceId }),
          ttsModel: form.ttsModel.trim() || undefined,
          isActive: form.isActive,
          fileId: fileId ?? undefined,
        },
      }).unwrap();
      props.toast.success('Voice updated');
      props.onOpenChange(false);
    } catch (err) {
      props.toast.error(errorMessage(err, 'Failed to update voice'));
    }
  };

  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Voice — {voice.name}</DialogTitle>
          <DialogDescription>Update the voice metadata and, optionally, replace its WAV sample.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-language">Language</Label>
              <Input
                id="edit-language"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                placeholder="es"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <textarea
              id="edit-description"
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="edit-provider-id">Provider Voice ID</Label>
              <Input
                id="edit-provider-id"
                value={form.providerVoiceId}
                onChange={(e) => setForm({ ...form, providerVoiceId: e.target.value })}
                placeholder="qwen-tts-vc-..."
              />
              <p className="text-xs text-muted-foreground">{PROVIDER_VOICE_HELP}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tts-model">TTS Model</Label>
              <Input
                id="edit-tts-model"
                value={form.ttsModel}
                onChange={(e) => setForm({ ...form, ttsModel: e.target.value })}
                placeholder={DEFAULT_TTS_MODEL}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-active"
              checked={form.isActive}
              onCheckedChange={(checked) => setForm({ ...form, isActive: checked === true })}
            />
            <Label htmlFor="edit-active">Active (available for synthesis)</Label>
          </div>
          <WavSampleUpload toast={props.toast} fileId={fileId} onFileIdChange={setFileId} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={isLoading}>{isLoading ? 'Saving…' : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Test synthesize dialog ───────────────────────────────────────────────────

function TestVoiceDialog(props: {
  voice: Voice;
  onOpenChange: (open: boolean) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { voice } = props;
  const [testVoice, { isLoading }] = useTestVoiceMutation();
  const { data: collections } = useGetStorageCollectionsQuery();
  const collectionList = (collections as StorageCollectionOption[] | undefined) ?? [];

  const [text, setText] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [result, setResult] = useState<VoiceTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!text.trim()) {
      props.toast.error('Enter text to synthesize');
      return;
    }
    setError(null);
    setResult(null);
    try {
      const res = await testVoice({
        id: getVoiceId(voice),
        text: text.trim(),
        collectionId: collectionId || undefined,
      }).unwrap();
      setResult(res);
      if (res.source === 'default') {
        props.toast.warning('Se usó la voz por defecto (no la clonada).');
      }
    } catch (err) {
      setError(describeTestError(err));
    }
  };

  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Test synthesize — {voice.name}</DialogTitle>
          <DialogDescription>Generate a short audio sample with this voice.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-text">Text</Label>
            <textarea
              id="test-text"
              autoFocus
              rows={3}
              className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="test-collection">Output collection</Label>
            <select
              id="test-collection"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
            >
              <option value="">Usar la colección por defecto del servicio</option>
              {collectionList.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Si el servicio responde 503, selecciona una colección de salida aquí.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={run} disabled={isLoading || !text.trim()}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Synthesize
            </Button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <SourceIndicator source={result?.source} providerVoiceId={voice.providerVoiceId} />
          {result && (
            <div className="space-y-1">
              <Label>Result</Label>
              {result.url && <audio controls src={result.url} className="w-full" />}
              <p className="text-xs text-muted-foreground">
                {result.durationMs ? `${(result.durationMs / 1000).toFixed(1)}s · ` : ''}
                file {result.fileId.slice(0, 12)}…
                {!result.url ? ' · sin URL reproducible' : ''}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Synthesize dialog (advanced: emitTo routing) ─────────────────────────────

function SynthesizeVoiceDialog(props: {
  voice: Voice;
  onOpenChange: (open: boolean) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { voice } = props;
  const [synthesizeVoice, { isLoading }] = useSynthesizeVoiceMutation();

  const [text, setText] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [emitKind, setEmitKind] = useState<'' | 'subject' | 'storage' | 'event'>('');
  const [emitTarget, setEmitTarget] = useState('');
  const [result, setResult] = useState<SynthesizeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!text.trim()) {
      props.toast.error('Enter text to synthesize');
      return;
    }
    if (emitKind && !emitTarget.trim()) {
      props.toast.error('Enter an emit target');
      return;
    }
    setError(null);
    setResult(null);
    try {
      const res = await synthesizeVoice({
        voiceId: getVoiceId(voice),
        text: text.trim(),
        conversationId: conversationId.trim() || undefined,
        emitTo: emitKind ? { kind: emitKind, target: emitTarget.trim() } : undefined,
      }).unwrap();
      setResult(res);
      if (res.source === 'default') {
        props.toast.warning('Se usó la voz por defecto (no la clonada).');
      }
    } catch (err) {
      setError(describeSynthesizeError(err));
    }
  };

  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Synthesize — {voice.name}</DialogTitle>
          <DialogDescription>
            Full synthesis; optionally route the result with an emit target.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="synth-text">Text</Label>
            <textarea
              id="synth-text"
              autoFocus
              rows={3}
              className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="synth-conversation">Conversation ID (optional)</Label>
            <Input
              id="synth-conversation"
              value={conversationId}
              onChange={(e) => setConversationId(e.target.value)}
              placeholder="Conversación asociada"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="synth-emit-kind">Emit to (optional)</Label>
              <select
                id="synth-emit-kind"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={emitKind}
                onChange={(e) => setEmitKind(e.target.value as '' | 'subject' | 'storage' | 'event')}
              >
                <option value="">No emit</option>
                <option value="subject">subject (service:method)</option>
                <option value="storage">storage (collectionId)</option>
                <option value="event">event (type)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="synth-emit-target">Emit target</Label>
              <Input
                id="synth-emit-target"
                value={emitTarget}
                onChange={(e) => setEmitTarget(e.target.value)}
                disabled={!emitKind}
                placeholder="agent-worker:audio.frame"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={run} disabled={isLoading || !text.trim()}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Waves className="mr-2 h-4 w-4" />}
              Synthesize
            </Button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <SourceIndicator source={result?.source} providerVoiceId={voice.providerVoiceId} />
          {result && (
            <div className="space-y-1">
              <Label>Result</Label>
              {result.url && <audio controls src={result.url} className="w-full" />}
              <p className="text-xs text-muted-foreground break-all">
                fileId: <span className="font-mono">{result.fileId}</span>
              </p>
              {result.url && <p className="text-xs text-muted-foreground break-all">url: {result.url}</p>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
